import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { log } from "utils/log";

import type { Drawing } from "../types";
import { drawTiledImage, type FitBox } from "./drawTiledImage";
import { fetchPreviewBitmap } from "./fetchPreviewBitmap";
import { TileBitmapCache } from "./tileBitmapCache";
import { getTiledSource } from "./tiledSource";
import { type TiledDrawingSource } from "./tileMath";

const l = log.miniMap.tiled;

/** 128 tiles ≈ 32 MB resident worst case (vs ~120 MB for the PDF route's bitmap). */
const LARGE_MAP_MAX_CACHED_TILES = 128;

/**
 * Per-paint tile budget; below the cache size so a paint never evicts its own
 * tiles (enforced as a hard cap in `drawTiledImage`, which stops touching
 * tiles past the budget even when `pickDrawLevel` bottomed out at `minLevel`).
 */
const LARGE_MAP_PAINT_TILE_BUDGET = Math.floor(LARGE_MAP_MAX_CACHED_TILES * 0.75);

/** Base-layer preview cap — admits the `medium` tier (1536 px ≈ 6.7 MB decoded). */
const LARGE_MAP_BASE_PREVIEW_MAX_SIZE = 1536;

export type TiledLargeMap =
    | { active: false }
    | {
          active: true;
          /** Natural (full-resolution) document size in pixels. */
          width: number;
          height: number;
          /**
           * Bumped as tiles / the base preview arrive — depend on it to
           * repaint. Coalesced to at most one bump per animation frame, so a
           * burst of resolving tiles costs one repaint, not one per tile.
           */
          version: number;
          /** Paint the base preview + visible tiles into `fitBox`. */
          draw: (
              ctx: CanvasRenderingContext2D,
              fitBox: FitBox,
              viewportWidth: number,
              viewportHeight: number,
              dpr: number,
          ) => void;
      };

/**
 * Viewport-driven tile painter for the large maps: a bounded tile LRU plus
 * one medium-preview base bitmap instead of the PDF route's full-resolution
 * decode. Pass `null` while closed to release everything. Returns
 * `{ active: false }` for non-tiled drawings (callers use the bitmap path).
 */
export function useTiledLargeMap(drawing: Pick<Drawing, "drawingData"> | null | undefined): TiledLargeMap {
    const source = getTiledSource(drawing?.drawingData.url);
    const [version, setVersion] = useState(0);
    const stateRef = useRef<{
        source: TiledDrawingSource;
        cache: TileBitmapCache;
        baseBitmap: ImageBitmap | undefined;
    } | null>(null);

    useEffect(() => {
        if (!source) {
            return;
        }
        // Tiles resolve from independent fetch/decode chains, so per-tile
        // version bumps would repaint the whole canvas once per tile.
        // Coalesce to one bump per animation frame instead.
        let repaintFrame = 0;
        const scheduleRepaint = () => {
            if (repaintFrame) {
                return;
            }
            repaintFrame = requestAnimationFrame(() => {
                repaintFrame = 0;
                setVersion((v) => v + 1);
            });
        };

        const cache = new TileBitmapCache(source, {
            maxTiles: LARGE_MAP_MAX_CACHED_TILES,
            onRepaintNeeded: scheduleRepaint,
        });
        const state: NonNullable<typeof stateRef.current> = { source, cache, baseBitmap: undefined };
        stateRef.current = state;
        setVersion((v) => v + 1);

        let disposed = false;
        // No decode cap: at cover fit the viewport wants ~2500 device px of
        // this base layer, so 1536 is already being upscaled — there is
        // nothing to trim here, unlike the mini map's crop.
        fetchPreviewBitmap(source, { tierMaxSize: LARGE_MAP_BASE_PREVIEW_MAX_SIZE })
            .then((bitmap) => {
                if (disposed) {
                    bitmap.close();
                    return;
                }
                state.baseBitmap = bitmap;
                scheduleRepaint();
            })
            .catch((error) => {
                // Non-fatal: tiles still paint, just without the blurry base.
                l.warn("Failed to load large map base preview:", error);
            });

        return () => {
            disposed = true;
            if (repaintFrame) {
                cancelAnimationFrame(repaintFrame);
            }
            cache.dispose();
            state.baseBitmap?.close();
            stateRef.current = null;
        };
    }, [source]);

    // Stable callback reading through the ref, so animation-frame closures
    // that captured an older `draw` still paint from the current cache.
    const draw = useCallback(
        (ctx: CanvasRenderingContext2D, fitBox: FitBox, viewportWidth: number, viewportHeight: number, dpr: number) => {
            const state = stateRef.current;
            if (!state) {
                return;
            }
            drawTiledImage({
                ctx,
                source: state.source,
                cache: state.cache,
                baseBitmap: state.baseBitmap,
                fitBox,
                viewportWidth,
                viewportHeight,
                dpr,
                tileBudget: LARGE_MAP_PAINT_TILE_BUDGET,
            });
        },
        [],
    );

    return useMemo<TiledLargeMap>(
        () =>
            source ? { active: true, width: source.width, height: source.height, version, draw } : { active: false },
        [source, version, draw],
    );
}
